import Skeleton from '@mui/material/Skeleton';
import { type JSX, useMemo, useState } from 'react';

import { HorizontalStackedBarChart } from 'Components/BaseCharts/HorizontalStackedBarChart';
import { Text } from 'Components/Text/Text';
import { DueDiligenceDashboardAssessmentSubject } from 'Models/Dashboards';
import { DueDiligenceModule, ProductModule } from 'Models/ProductModule';
import { riskRatingLabelAsNumber } from 'Models/RiskRating';

import styles from './RiskProgressCharts.module.css';
import { AssessmentSubjectsDisplayModal, SelectedAssessmentSubjectDetails } from '../AssessmentSubjectsDisplayModal/AssessmentSubjectsDisplayModal';
import { buildRiskRatingDataArray } from '../DueDiligenceDashboard.helpers';

enum Modals {
    NONE,
    DISPLAY_ASSESSMENT_SUBJECTS,
}

export interface RiskProgressChartsProps {
    module: DueDiligenceModule;

    assessmentSubjects?: DueDiligenceDashboardAssessmentSubject[];
    assessmentSubjectsError?: string;
}

/**
 * Shows the user which third-party services (or, for UCRM, use cases) have not been scheduled to be (re-)assessed.
 * Assessment subjects with due diligence workflow due dates are filtered out, and the remaining ones are categorized both by inherent risk and by residual risk. The point is that users can tell at a glance whether high-risk assessment subjects are not scheduled to be (re-)assessed.
 *
 * TODO: Give this component a better name.
 */
export const RiskProgressCharts = (props: RiskProgressChartsProps): JSX.Element => {
    const [modalState, setModalState] = useState<Modals>(Modals.NONE);
    const [assessmentSubjectsModalSubtitle, setAssessmentSubjectsModalSubtitle] = useState<string>();
    const [selectedAssessmentSubjects, setSelectedAssessmentSubjects] = useState<SelectedAssessmentSubjectDetails[]>();

    const inherentRiskProgressChartData = useMemo(() => {
        if (props.assessmentSubjects) {
            const filteredScores: number[] = props.assessmentSubjects.filter((assessmentSubject) => assessmentSubject.assessment_workflow_setup.due_date === undefined).map((assessmentSubject) => assessmentSubject.inherent_risk_score ?? 0);
            return buildRiskRatingDataArray(filteredScores);
        }
    }, [props.assessmentSubjects]);

    const residualRiskProgressChartData = useMemo(() => {
        if (props.assessmentSubjects) {
            const filteredScores: number[] = props.assessmentSubjects.filter((assessmentSubject) => assessmentSubject.assessment_workflow_setup.due_date === undefined).map((assessmentSubject) => assessmentSubject.assessment_workflow_data.final_review_human_assessment.residual_risk ?? 0);
            return buildRiskRatingDataArray(filteredScores);
        }
    }, [props.assessmentSubjects]);

    const handleInherentRiskChartClick = (targetRating: string): void => {
        if (targetRating) {
            const targetRatingScore = riskRatingLabelAsNumber(targetRating);
            setAssessmentSubjectsModalSubtitle(targetRating);
            setModalState(Modals.DISPLAY_ASSESSMENT_SUBJECTS);
            setSelectedAssessmentSubjects(
                props
                    .assessmentSubjects!.filter((assessmentSubject) => assessmentSubject.assessment_workflow_setup.due_date === undefined)
                    .filter((assessmentSubject) => (assessmentSubject.inherent_risk_score ?? 0) === targetRatingScore)
                    .map((assessmentSubject) => ({
                        assessmentSubjectId: assessmentSubject.id,
                        assessmentSubjectName: assessmentSubject.name,
                        organizationId: assessmentSubject.vendor_id,
                        organizationName: assessmentSubject.vendor_name,
                    }))
            );
        }
    };

    const handleResidualRiskChartClick = (targetRating: string): void => {
        if (targetRating) {
            const targetRatingScore = riskRatingLabelAsNumber(targetRating);
            setAssessmentSubjectsModalSubtitle(targetRating);
            setModalState(Modals.DISPLAY_ASSESSMENT_SUBJECTS);
            setSelectedAssessmentSubjects(
                props
                    .assessmentSubjects!.filter((assessmentSubject) => assessmentSubject.assessment_workflow_setup.due_date === undefined)
                    .filter((assessmentSubject) => (assessmentSubject.assessment_workflow_data.final_review_human_assessment.residual_risk ?? 0) === targetRatingScore)
                    .map((assessmentSubject) => ({
                        assessmentSubjectId: assessmentSubject.id,
                        assessmentSubjectName: assessmentSubject.name,
                        organizationId: assessmentSubject.vendor_id,
                        organizationName: assessmentSubject.vendor_name,
                    }))
            );
        }
    };

    const handleAssessmentSubjectsModalClose = () => {
        setModalState(Modals.NONE);
        setAssessmentSubjectsModalSubtitle(undefined);
        setSelectedAssessmentSubjects(undefined);
    };

    if (props.assessmentSubjectsError) {
        return (
            <div className={styles.notScheduledCharts}>
                <div>
                    <Text variant="Text2" color="darkGray">
                        INHERENT RISK
                    </Text>
                    <Text color="white">{props.assessmentSubjectsError}</Text>
                </div>
                <div>
                    <Text variant="Text2" color="darkGray">
                        RESIDUAL RISK
                    </Text>

                    <Text color="white">{props.assessmentSubjectsError}</Text>
                </div>
            </div>
        );
    }

    const content = (() => {
        const hasNoAssessmentSubjects = props.assessmentSubjects && props.assessmentSubjects.length === 0;
        const hasAssessmentSubjectsAndAllAreScheduled = inherentRiskProgressChartData !== undefined && residualRiskProgressChartData !== undefined && inherentRiskProgressChartData.every((data) => data.value === 0) && residualRiskProgressChartData.every((data) => data.value === 0);

        if (hasNoAssessmentSubjects) {
            return (
                <Text variant="Text3" color="white">
                    {props.module === ProductModule.UCRM ? 'No use cases have been created.' : 'No third-party services have been created.'}
                </Text>
            );
        } else if (hasAssessmentSubjectsAndAllAreScheduled) {
            return (
                <Text variant="Text3" color="white">
                    {props.module === ProductModule.UCRM ? 'All use cases have been scheduled for assessment.' : 'All third-party services have been scheduled for assessment.'}
                </Text>
            );
        } else {
            return (
                <div className={styles.notScheduledCharts}>
                    <div>
                        <Text variant="Text2" color="darkGray">
                            INHERENT RISK
                        </Text>
                        {inherentRiskProgressChartData ? <HorizontalStackedBarChart name="Inherent Risk Score" bars={inherentRiskProgressChartData} onChartClick={handleInherentRiskChartClick} chartDimensions={{ width: '100%', height: 50 }} /> : <Skeleton variant="rounded" width={'100%'} height={50} />}
                    </div>
                    <div>
                        <Text variant="Text2" color="darkGray">
                            RESIDUAL RISK
                        </Text>
                        {residualRiskProgressChartData ? <HorizontalStackedBarChart name="Residual Risk Score" bars={residualRiskProgressChartData} onChartClick={handleResidualRiskChartClick} chartDimensions={{ width: '100%', height: 50 }} /> : <Skeleton variant="rounded" width={'100%'} height={50} />}{' '}
                    </div>
                </div>
            );
        }
    })();

    return (
        <>
            {modalState === Modals.DISPLAY_ASSESSMENT_SUBJECTS && assessmentSubjectsModalSubtitle && selectedAssessmentSubjects && <AssessmentSubjectsDisplayModal hideModal={handleAssessmentSubjectsModalClose} module={props.module} subtitle={assessmentSubjectsModalSubtitle} selectedAssessmentSubjects={selectedAssessmentSubjects} />}
            {content}
        </>
    );
};
